import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Pencil, MinusCircle, Loader2 } from "lucide-react";
import { api, errMsg, money, fmtDate } from "@/lib/api";

export async function openPdf(url: string) {
  const w = window.open("", "_blank");
  try {
    const r = await api.get(url, { responseType: "blob" });
    const href = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    if (w) { w.location.href = href; } else { window.location.href = href; }
  } catch (e) {
    w?.close();
    toast.error(errMsg(e));
  }
}

function RemoveButton({ r, onDone }: { r: any; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    const reason = window.prompt(`Remove ${r.first_name} ${r.last_name} from this bill? Add a short reason (required):`);
    if (!reason?.trim()) return;
    setBusy(true);
    try {
      await api.patch(`/admin/cases/${r.case_id}/billing-omit`, { omit: true, reason: reason.trim() });
      toast.success(`${r.first_name} ${r.last_name} removed from the bill. You can restore them under Omitted from billing.`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <button className="btn-secondary btn-sm text-[#B91C1C]" disabled={busy} onClick={go} data-testid={`billing-preview-remove-${r.case_id}`}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MinusCircle className="h-4 w-4" />} Remove
    </button>
  );
}

export function StatementPreview({ o, month, onChanged }: { o: any; month: string; onChanged: () => void }) {
  return (
    <div className="border-t border-[#E2E8F0] bg-[#F8FAFC] p-5" data-testid={`billing-preview-${o.organization.id}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[#475569]">
          {o.organization.parent_org_id
            ? <>This statement is emailed to <b>{o.organization.email || "no email on file"}</b> when billing runs.</>
            : <>Statement preview for <b>{o.organization.name}</b>.</>}
        </p>
        <button className="btn-secondary btn-sm" onClick={() => openPdf(`/admin/billing/${month}/organizations.pdf?org_id=${o.organization.id}`)} data-testid={`billing-preview-pdf-${o.organization.id}`}><Eye className="h-4 w-4" /> View statement PDF</button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-[#E2E8F0] bg-white">
        <table className="tbl">
          <thead><tr><th>Applicant</th><th>Type</th><th>Returned</th><th>Parish</th><th>Results</th><th>Amount</th><th /></tr></thead>
          <tbody>
            {o.rows.map((r: any) => (
              <tr key={r.case_id} data-testid={`billing-preview-row-${r.case_id}`}>
                <td><Link to={`/admin/cases/${r.case_id}`} className="font-semibold text-[#1B365D] hover:underline">{r.last_name}, {r.first_name}</Link>{r.billing_note && <p className="text-xs text-[#94A3B8]">{r.billing_note}</p>}
                  {(r.extra_fees || []).map((f: any, i: number) => <p key={i} className="text-xs text-[#1B365D]" data-testid={`billing-preview-extra-${r.case_id}-${i}`}>+ {f.description}: {money(f.amount)}</p>)}</td>
                <td className="text-xs">{r.type}{r.ny && <span className="ml-1 rounded-full bg-[#FEE2E2] px-1.5 py-0.5 text-[10px] font-bold text-[#B91C1C]">NY</span>}</td>
                <td className="font-mono text-xs">{fmtDate(r.date_rtn)}</td>
                <td className="text-xs">{r.parish || "—"}</td>
                <td className="text-xs">{r.results}</td>
                <td className="font-mono text-sm font-semibold">{money(r.total_due)}</td>
                <td className="whitespace-nowrap text-right">
                  <Link to={`/admin/cases/${r.case_id}/edit`} className="btn-secondary btn-sm mr-2" data-testid={`billing-preview-edit-${r.case_id}`}><Pencil className="h-4 w-4" /> Edit</Link>
                  <RemoveButton r={r} onDone={onChanged} />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr><td colSpan={5} className="text-right text-sm font-semibold">Total due</td><td className="font-mono font-bold">{money(o.total_due)}</td><td /></tr></tfoot>
        </table>
      </div>
    </div>
  );
}
